import { UserInfo } from '../shared/models/user';
import { LoginApiOptions } from '../shared/models/login';
import { Organization } from '../shared/models/organization';
import { ToastInfo } from '../shared/models/toast';
import { VerifyResponse } from '../shared/models/verify';
import { Application } from '../shared/models/application';
import { BulkUploadTaskContainer, Job, JobsUploadError, JobsUploadStatus, SearchJobsParams } from '../shared/models/jobs';
import { JourneyConfig, PreviousJourneyConfig } from '../shared/models/journey';
import { ModalState } from '../shared/models/modal';
import { InsightsData, InsightsParams } from '../shared/models/insights';
import { ChatData } from '../shared/models/chat';
import { Session } from '../shared/models/session';
import { InternalSignUpState } from '../i-admin/reducers/internalSignUp.reducer';

export interface RootState {
  profile: ProfileState;
  login: LoginState;
  restore: RestorePasswordState;
  userManagement: UserManagementState;
  orgHierarchy: OrgHierarchyState;
  toasts: ToastState;
  jobs: {
    search: JobsSearchState;
    jobs: JobsState;
    uploads: JobsUploadState;
    updateJob: UpdateJobState;
    rows: UploadRowsState;
  },
  sessions: SessionsState;
  orgManagement: {
    verify: VerifyState;
    register: RegisterState;
    application: ApplicationState;
  };
  journey: {
    config: JourneyConfigState;
  };
  modal: ModalState;
  insights: InsightsState;
  billing: BillingState;
  chat: ChatState;
  internalSignUp: InternalSignUpState;
}

export interface ProfileState {
  userInfo: UserInfo;
  isLoading: boolean;
  saving: boolean;
  saved: boolean;
  error: string;
}

export interface InsightsState {
  data: InsightsData;
  filter: InsightsParams;
  isLoading: boolean;
  loaded: boolean;
  error: string;
}

export interface UserManagementState {
  users: {
    data: UserInfo[];
    isLoading: boolean;
    isRemoving: boolean;
    error: string;
  },
  userDetails: {
    data: UserInfo;
    isLoading: boolean;
    error: string;
  },
  updateUser: UpdateUserState;
}

export interface UpdateUserState {
  user?: UserInfo;
  loading: boolean;
  saving: boolean;
  saved: boolean;
  error: string;
}

export interface JourneyConfigState {
  configID: string;
  data: JourneyConfig;
  previous: PreviousJourneyConfig;
  isLoading: boolean;
  loaded: boolean;
  error: string;
  saving: boolean;
}

export interface JobsSearchState {
  data: Job[];
  next: string;
  params: SearchJobsParams;
  previous: string;
  total_tasks: number;
  isLoading: boolean;
  error: string;
}

export interface SessionsState {
  data: Session[];
  isLoading: boolean;
  error: string;
}

export interface JobsState {
  data: Job[];
  isLoading: boolean;
  error: string;
}

export interface JobsUploadState {
  data: JobsUploadStatus[];
  isLoading: boolean;
  isUploading: boolean;
  uploadErrors: JobsUploadError;
}

export interface UpdateJobState {
  job?: Job;
  loading: boolean;
  saving: boolean;
  saved: boolean;
  error: string;
}

export interface UploadRowsState {
  data: BulkUploadTaskContainer[];
  loading: boolean;
  error: string;
}

export interface LoginState {
  apiOptions: LoginApiOptions;
  authed: string;
  errorText: string;
  sessionToken: string;
  sessionTokenExpiration: Date;
  isLoading: boolean;
}

export interface RestorePasswordState {
  success: boolean;
  isLoading: boolean;
  errorText: string;
}

export interface RegisterState {
  registered: boolean;
  errorText: string;
  isLoading: boolean;
}

export interface VerifyState {
  data: VerifyResponse;
  hasError: boolean;
  errorText: string;
  isLoading: boolean;
}

export interface ApplicationState {
  data: Application;
  hasError: boolean;
  errorText: string;
  isLoading: boolean;
}

export interface OrgHierarchyState {
  selectedOrg: Organization;
  activeNode: Organization;
  rootOrg: Organization;
  orgs: Organization[];
  isLoading: boolean;
  loaded: boolean;
  saving: boolean;
  saved: boolean;
  error: string;
  creating: boolean;
  created: boolean;
}

export interface ToastState {
  toasts: ToastInfo[],
}

export interface BillingState {
  sync: boolean;
}


export interface ChatState {
  connecting: boolean;
  connected: boolean;
  chats: {
    [chat_channel: string]: ChatData
  }
  error: string;
}

const initialState: Partial<RootState> = {
  profile: null,
  userManagement: null,
  login: null,
  orgHierarchy: null,
  restore: null,
  toasts: {
    toasts: [],
  },
  jobs: {
    jobs: null,
    search: null,
    uploads: null,
    updateJob: null,
    rows: null,
  },
  orgManagement: {
    verify: null,
    register: null,
    application: null,
  },
  journey: {
    config: null,
  },
  modal: {
    name: null,
    data: null,
  },
  insights: null,
  billing: null,
  chat: null,
  sessions: null,
  internalSignUp: null,
};

export default initialState;
